
<template>
  <div>
    <intervention-change-status
      :intervention="intervention"
      @close="isChangingStatus = false"
      @updated="statusChanged"
      v-if="isChangingStatus"
    />
    <intervention-add-comment
      :intervention="intervention"
      @close="isAddingComment = false"
      @added="commentAdded"
      v-if="isAddingComment"
    />
    <close-intervention
      :intervention="intervention"
      @close="isClosingIntervention = false"
      @added="interventionClosed"
      v-if="isClosingIntervention"
    />
    <intervention-change-assignee
      :intervention="intervention"
      @close="isChangingAssignee = false"
      @updated="assigneeChanged"
      v-if="isChangingAssignee"
    />
    <intervention-date-dialog
      v-model="isInterventionDateDialogOpened"
      :intervention="intervention"
      @close="isInterventionDateDialogOpened = false"
      @updated="interventionDateChanged"
    />
    <intervention-add-file
      :intervention="intervention"
      @close="isAddingAttachment = false"
      @added="attachmentAdded"
      attachment
      v-if="isAddingAttachment"
    />
    <intervention-add-file
      :intervention="intervention"
      @close="isAddingEstimateOrInvoice = false"
      @added="estimateOrInvoiceAdded"
      v-if="isAddingEstimateOrInvoice"
    />
    <equipment-modal
      :updatable="false"
      @close="isEquipmentModalOpened = false"
      v-if="isEquipmentModalOpened"
    />
    <report-add-dialog
      v-model="isAddingReportDialogOpened"
      :intervention-id="intervention.id"
      @close="closeAddReport"
      @created="reportCreated"
      v-if="isAddingReportDialogOpened"
    />
    <report-dialog
      v-model="isReportDialogOpened"
      :report-id="openedReportId"
      @close="closeReport"
      v-if="isReportDialogOpened"
    />
    <confirm-dialog
      v-model="isConfirmRemoveInterventionDialogOpened"
      text="Êtes-vous sûr de vouloir supprimer cette intervention ?"
      wait-after-answer
      :loading="isRemovingIntervention"
      @yes="removeIntervention"
    />
    <v-row no-gutters justify="center" align="start">
      <div class="ml-6 mr-6 n-container">
        <v-row no-gutters align="center" class="content-header">
          <v-btn class="n-white-btn n-small-btn px-2 mr-2" @click="goToInterventions()" v-if="!inDialog">
            <v-icon small class="mr-2">chevron_left</v-icon>
            Retour interventions
          </v-btn>
          <span class="font-weight-bold primary--text pl-2" v-if="hasIntervention">
            Intervention {{ intervention.displayNum }}
          </span>
          <v-spacer></v-spacer>
          <v-btn target="_blank" :href="`${apiBaseUrl}/interventions/${intervention.id}/${intervention.num}.pdf`" class="n-white-btn n-small-btn px-2" v-if="hasIntervention">
            <v-icon small class="mr-2">print</v-icon>
            Imprimer
          </v-btn>
          <v-menu
            :min-width="300"
            offset-y
            bottom
            v-if="canAddComment || canAddAttachment || canDoOtherThings"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                dark
                color="primary"
                elevation="0"
                class="n-small-btn n-primary-btn ml-3"
                v-bind="attrs"
                v-on="on"
                @click="isNewUserDialogOpened = true"
              >
                Actions
                <v-icon right>arrow_drop_down</v-icon>
              </v-btn>
            </template>
            <n-card>
              <v-list dense class="py-0">
                <template v-if="canChangeStatus">
                  <v-list-item @click="isChangingStatus = true">
                    <v-list-item-avatar size="30">
                      <v-icon>flag</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Modifier le statut
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canAddComment">
                  <v-divider />
                  <v-list-item @click="isAddingComment = true">
                    <v-list-item-avatar size="30">
                      <v-icon>insert_comment</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Ajouter un commentaire
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canAddNote">
                  <v-divider />
                  <v-list-item @click="openNewNote">
                    <v-list-item-avatar size="30">
                      <v-icon>sticky_note_2</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Ajouter une note interne
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canChangeAssignee">
                  <v-divider />
                  <v-list-item @click="isChangingAssignee = true">
                    <v-list-item-avatar size="30">
                      <v-icon>person_add</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Assigner à un utilisateur
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canAddAttachment">
                  <v-divider />
                  <v-list-item @click="isAddingAttachment = true">
                    <v-list-item-avatar size="30">
                      <v-icon>attach_file</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Ajouter une pièce jointe
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canAddEstimateOrInvoice">
                  <v-divider />
                  <v-list-item @click="isAddingEstimateOrInvoice = true">
                    <v-list-item-avatar size="30">
                      <v-icon>paid</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Ajouter un devis / facture
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canAddReport">
                  <v-divider />
                  <v-list-item @click="isAddingReportDialogOpened = true">
                    <v-list-item-avatar size="30">
                      <v-icon>engineering</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Créer un compte-rendu
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canCloseIntervention">
                  <v-divider />
                  <v-list-item @click="isClosingIntervention = true">
                    <v-list-item-avatar size="30">
                      <v-icon>pan_tool</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Clôturer l'intervention
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-if="canRemoveIntervention">
                  <v-divider />
                  <v-list-item @click="openRemoveInterventionConfirmDialog">
                    <v-list-item-avatar size="30">
                      <v-icon>delete</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      Supprimer l'intervention
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </n-card>
          </v-menu>
        </v-row>
        <v-row no-gutters justify="start" align="start" v-if="intervention">
          <v-col class="page-main-content">
            <v-alert type="warning" class="mb-4" v-if="isChallenging">
              <v-row no-gutters justify="center" align="center">
                <v-col>
                  Une mise en concurrence est en cours
                </v-col>
                <v-btn small outlined color="white" @click="goToChallengingIntervention()">
                  <v-icon small class="mr-2">visibility</v-icon>
                  Voir
                </v-btn>
              </v-row>
            </v-alert>
            <intervention-card
              :value="intervention"
              tag-admin
              @tag-added="tagAdded"
              @tag-removed="tagRemoved"
            />
            <v-row no-gutters justify="center" align="start" v-if="hasIntervention">
              <v-col>
                <v-row no-gutters justify="start" align="center" class="mt-6 ml-1">
                  <v-icon color="accent">history</v-icon>
                  <h3 class="ml-2 primary--text">Progression</h3>
                </v-row>
                <div>
                  <template v-for="(step, i) in flavoredSteps">
                    <step-card :key="step.id" :value="step" :show-status="i === 0 || step.Status_id !== flavoredSteps[i - 1].Status_id" />
                  </template>
                  <v-row no-gutters justify="center" align="center" v-if="!flavoredSteps.length">
                    <v-progress-linear
                      color="primary"
                      indeterminate
                      rounded
                      height="6"
                      class="mt-10"
                      style="max-width: 200px;"
                    />
                  </v-row>
                  <v-row no-gutters justify="center" align="center" class="mt-6" v-if="canAddComment && flavoredSteps.length">
                    <v-btn class="n-white-btn n-small-btn px-12" @click="isAddingComment = true">
                      <v-icon small class="mr-2">insert_comment</v-icon>
                      Ajouter un commentaire
                    </v-btn>
                  </v-row>
                </div>
              </v-col>
              <v-col class="pl-4" style="max-width: 360px;" v-if="!isSelfIntervention">
                <v-row no-gutters justify="start" align="center" class="mt-6 ml-1">
                  <v-icon color="accent">sticky_note_2</v-icon>
                  <h3 class="ml-2 primary--text">Notes internes</h3>
                </v-row>
                <div class="mt-6">
                  <v-row no-gutters justify="center" align="center" class="mt-2" v-if="isFetchingNotes">
                    <v-progress-linear
                      color="primary"
                      indeterminate
                      rounded
                      height="6"
                      class="mt-10"
                      style="max-width: 200px;"
                    />
                  </v-row>
                  <template v-else>
                    <v-row no-gutters justify="center" align="center" class="pt-6 pb-2" v-if="!notes.length">
                      <v-icon color="grey">sticky_note_2</v-icon>
                      <div class="grey--text text--darken-1 pl-2 font-weight-medium">Aucune note interne</div>
                    </v-row>
                    <template v-for="note in notes">
                      <note-card
                        :key="note.id"
                        :value="note"
                        @updated="noteUpdated"
                      />
                    </template>
                    <div class="mt-6">
                      <v-expand-transition>
                        <v-row no-gutters justify="center" align="center" v-show="canAddNote && !isAddingNote">
                          <v-btn class="n-white-btn n-small-btn px-12" @click="openNewNote">
                            <v-icon small class="mr-2">note_alt</v-icon>
                            Nouvelle note
                          </v-btn>
                        </v-row>
                      </v-expand-transition>
                      <v-expand-transition>
                        <div v-show="isAddingNote">
                          <v-textarea
                            v-model="newNoteComment"
                            ref="newNoteTextarea"
                            hide-details
                            no-resize
                            solo
                            height="140"
                            background-color="rgba(255, 214, 65, 0.1)"
                            :disabled="isSavingNewNote"
                          />
                          <v-row no-gutters justify="end" align="center" class="mt-3">
                            <v-btn small tabindex="-1" text :disabled="isSavingNewNote" @click="isAddingNote = false">Annuler</v-btn>
                            <v-btn small color="primary darken-1" :loading="isSavingNewNote" @click="addNote">Ajouter</v-btn>
                          </v-row>
                        </div>
                      </v-expand-transition>
                    </div>
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-col>
          <div class="right-pane ml-6">
            <n-card>
              <n-card-header class="primary--text">Lieu d'intervention</n-card-header>
              <div class="px-4 pb-4 pt-1">
                <template v-if="!isSelfIntervention">
                  <n-card-title>Client</n-card-title>
                  <n-card-item>{{ intervention.client.name }}</n-card-item>
                </template>
                <n-card-title>Site</n-card-title>
                <n-card-item>{{ intervention.site.name }}</n-card-item>
                <template v-if="intervention.site.ref && intervention.site.ref.length">
                  <n-card-title>Référence site</n-card-title>
                  <n-card-item>{{ intervention.site.ref }}</n-card-item>
                </template>
                <template v-if="intervention.site.$address.length">
                  <n-card-title>Adresse</n-card-title>
                  <n-card-item>{{ intervention.site.$address }}</n-card-item>
                </template>
                <template v-if="hasPlaceDetails">
                  <n-card-title>Détails du lieu</n-card-title>
                  <n-card-item class="pl-2">
                    <div v-if="intervention.$placeDetails">
                      {{ intervention.placeDetails }}
                    </div>
                    <v-row no-gutters justify="start" align="center" v-if="intervention.$floor || intervention.$room">
                      <v-col cols="5" v-if="intervention.$floor">
                        <span class="font-weight-regular">Étage</span> : {{ intervention.floor }}
                      </v-col>
                      <v-col cols="7" v-if="intervention.$room">
                        <span class="font-weight-regular">Pièce</span> : {{ intervention.room }}
                      </v-col>
                    </v-row>
                  </n-card-item>
                </template>
                <template v-if="isRealEstateClient">
                  <template v-if="intervention.site.$tenant.length">
                    <n-card-title>Occupant</n-card-title>
                    <n-card-item>
                      <div>{{ intervention.site.$tenant }}</div>
                      <div class="text-caption pl-2" v-if="intervention.site.$tenantPhone1.length">
                        <v-icon small>phone</v-icon>
                        <a class="ml-1" :href="`tel:${intervention.site.$tenantPhone1}`">{{ intervention.site.$tenantPhone1 }}</a>
                        <template v-if="intervention.site.$tenantPhone2.length">
                          &nbsp;/&nbsp;
                          <a :href="`tel:${intervention.site.$tenantPhone2}`">{{ intervention.site.$tenantPhone2 }}</a>
                        </template>
                      </div>
                      <div class="text-caption pl-2" v-if="intervention.site.$tenantEmail.length">
                        <v-icon small>mail_outline</v-icon>
                        <a class="ml-1" :href="`mailto:${intervention.site.$tenantEmail}`">{{ intervention.site.$tenantEmail }}</a>
                      </div>
                    </n-card-item>
                  </template>
                  <template v-if="intervention.site.$owner.length">
                    <n-card-title>Propriétaire</n-card-title>
                    <n-card-item>{{ intervention.site.$owner }}</n-card-item>
                  </template>
                </template>
                <template v-if="intervention.site.$phone1.length">
                  <n-card-title>Téléphone</n-card-title>
                  <n-card-item>{{ intervention.site.$phone1 }}</n-card-item>
                </template>
              </div>
            </n-card>
            <n-card class="mt-3">
              <n-card-header class="primary--text">Plannification</n-card-header>
              <div class="px-4 pb-3">
                <n-card-title class="pb-2" v-if="isUpdatingInterventionDate">
                  <v-row no-gutters justify="center" align="center">
                    <v-progress-linear
                      color="primary"
                      indeterminate
                      rounded
                      height="6"
                    />
                  </v-row>
                </n-card-title>
                <n-card-title v-else>
                  <v-row no-gutters justify="start" align="center" class="mt-3">
                    <v-col class="ellipsis">
                      <a @click="isInterventionDateDialogOpened = true" class="attachment-link" v-if="canUpdateInterventionDate">
                        <v-icon color="primary darken-2" class="pr-1" style="margin-top: -4px; font-size: 20px;">event</v-icon>
                        <div class="attachment-filename pl-1" v-if="hasInterventionDate">
                          {{ intervention | formatInterventionDate }}
                        </div>
                        <div class="attachment-filename pl-1" v-else>Définir une date d'intervention</div>
                      </a>
                      <v-row no-gutters justify="start" align="center" v-else>
                        <v-icon color="primary darken-2" class="pr-1" style="margin-top: -4px; font-size: 20px;">event</v-icon>
                        <div class="attachment-filename pl-1" v-if="hasInterventionDate">
                          {{ intervention | formatInterventionDate }}
                        </div>
                        <div class="attachment-filename pl-1" v-else>Aucune date d'intervention définie</div>
                      </v-row>
                    </v-col>
                  </v-row>
                </n-card-title>
              </div>
            </n-card>
            <n-card class="mt-3" v-if="isEquipmentsActivated">
              <n-card-header class="pr-2">
                <v-row no-gutters justify="center" align="center">
                  <v-row no-gutters justify="start" align="center">
                    <span class="primary--text">Équipements</span>
                    <div class="intervention-nb-indicator" v-if="!isFetchingLinkedEquipments">{{ linkedEquipments.length }}</div>
                  </v-row>
                  <v-menu
                    v-model="availableEquipmentsMenu"
                    min-width="450"
                    max-width="450"
                    offset-y
                    :close-on-content-click="false"
                    bottom
                    v-if="canLinkEquipment"
                  >
                    <template v-slot:activator="{ attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        @click="openAvailableEquipmentsMenu"
                      >
                        <v-icon>add</v-icon>
                      </v-btn>
                    </template>
                    <n-card>
                      <div class="px-4 py-6" v-if="isFetchingAvailableEquipments">
                        <v-progress-linear
                          color="primary"
                          indeterminate
                          rounded
                          height="6"
                        ></v-progress-linear>
                      </div>
                      <v-list dense class="py-0 overflow-y-auto" max-height="400px" v-else>
                        <div class="pa-3">
                          <v-text-field
                            v-model="searchAvailableEquipments"
                            outlined
                            solo
                            flat
                            hide-details
                            autofocus
                            prepend-inner-icon="search"
                            placeholder="Chercher un équipement"
                          />
                        </div>
                        <div class="pa-5 font-italic" v-if="!availableEquipments.length">
                          Aucun équipement trouvé
                        </div>
                        <template v-for="equipment in availableEquipments">
                          <v-list-item :key="equipment.id" @click="linkEquipment(equipment)">
                            <v-list-item-content>
                              <v-list-item-title>
                                <template v-if="equipment.number">
                                  {{ equipment.number }}
                                  <span class="px-1">-</span>
                                </template>
                                {{ equipment.label }}
                              </v-list-item-title>
                              <v-list-item-subtitle v-if="equipment.model || equipment.brand">
                                <template v-if="equipment.brand">
                                  {{ equipment.brand }}
                                </template>
                                <template v-if="equipment.model && equipment.brand">
                                  <span class="px-1">-</span>
                                </template>
                                <template v-if="equipment.model">
                                  {{ equipment.model }}
                                </template>
                              </v-list-item-subtitle>
                              <v-list-item-subtitle>
                                <template v-if="equipment.serialNumber">
                                  {{ equipment.serialNumber }}
                                </template>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider :key="`divider-${equipment.id}`" />
                        </template>
                      </v-list>
                    </n-card>
                  </v-menu>
                </v-row>
              </n-card-header>
              <div class="px-4 py-6" v-if="isFetchingLinkedEquipments">
                <v-progress-linear
                  color="primary"
                  indeterminate
                  rounded
                  height="6"
                ></v-progress-linear>
              </div>
              <div class="px-4 pb-3" v-else-if="linkedEquipments.length">
                <v-row no-gutters justify="start" align="center" v-for="equipment in linkedEquipments" :key="equipment.id" class="mt-3">
                  <v-col class="ellipsis">
                    <a @click="openEquipment(equipment)" class="attachment-link">
                      <v-icon color="primary darken-2" class="pr-1" style="margin-top: -4px; font-size: 20px;">memory</v-icon>
                      <div class="attachment-filename pl-1">
                        <template v-if="equipment.number && equipment.number.length">{{ equipment.number }} - </template>
                        {{ equipment.label }}
                      </div>
                    </a>
                  </v-col>
                  <v-icon class="attachment-delete" @click="equipmentToUnlink = equipment" v-if="canUnlinkEquipment">delete</v-icon>
                </v-row>
              </div>
              <div class="pa-4 font-italic" v-else>
                Aucun équipement concerné
              </div>
            </n-card>
            <n-card class="mt-3">
              <n-card-header class="pr-2">
                <v-row no-gutters justify="center" align="center">
                  <v-row no-gutters justify="start" align="center">
                    <span class="primary--text">CR d'interventions</span>
                    <div class="intervention-nb-indicator" v-if="!isFetchingReports">{{ reports.length }}</div>
                  </v-row>
                  <v-btn
                    icon
                    v-if="canAddReport"
                    @click="isAddingReportDialogOpened = true"
                  >
                    <v-icon>add</v-icon>
                  </v-btn>
                </v-row>
              </n-card-header>
              <div class="px-4 py-6" v-if="isFetchingReports">
                <v-progress-linear
                  color="primary"
                  indeterminate
                  rounded
                  height="6"
                ></v-progress-linear>
              </div>
              <div class="px-4 pb-3" v-else-if="reports.length">
                <template v-for="(report, i) in reports">
                  <v-row no-gutters justify="start" align="center" :key="`report-${i}`" class="mt-3">
                    <v-col class="ellipsis">
                      <a @click="openReport(report)" class="attachment-link">
                        <font-awesome-icon :icon="[`far`, `sticky-note`]" class="report-icon mr-2" />
                        <div class="attachment-filename pl-1">
                          Le {{ report.$date }} à {{ report.$hour }}
                        </div>
                      </a>
                    </v-col>
                  </v-row>
                </template>
              </div>
              <div class="pa-4 font-italic" v-else>
                Aucune pièce jointe
              </div>
            </n-card>
            <n-card class="mt-3">
              <n-card-header class="pr-2">
                <v-row no-gutters justify="center" align="center">
                  <v-row no-gutters justify="start" align="center">
                    <span class="primary--text">Pièces jointes</span>
                    <div class="intervention-nb-indicator" v-if="!isFetchingFiles">{{ files.length }}</div>
                  </v-row>
                  <v-menu
                    :min-width="300"
                    offset-y
                    bottom
                    v-if="canDoOtherThings && canAddAttachment"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-if="canAddAttachment"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>add</v-icon>
                      </v-btn>
                    </template>
                    <n-card>
                      <v-list dense class="py-0">
                        <v-list-item @click="isAddingAttachment = true">
                          <v-list-item-avatar size="30">
                            <v-icon>attach_file</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            Ajouter une pièce jointe
                          </v-list-item-content>
                        </v-list-item>
                        <template v-if="canAddEstimateOrInvoice">
                          <v-divider />
                          <v-list-item @click="isAddingEstimateOrInvoice = true">
                            <v-list-item-avatar size="30">
                              <v-icon>paid</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              Ajouter un devis / facture
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-list>
                    </n-card>
                  </v-menu>
                  <v-btn icon @click="isAddingAttachment = true" v-else-if="canAddAttachment">
                    <v-icon>add</v-icon>
                  </v-btn>
                </v-row>
              </n-card-header>
              <div class="px-4 py-6" v-if="isFetchingFiles">
                <v-progress-linear
                  color="primary"
                  indeterminate
                  rounded
                  height="6"
                ></v-progress-linear>
              </div>
              <div class="px-4 pb-3" v-else-if="files.length">
                <template v-for="(attachment, i) in files">
                  <v-row no-gutters justify="start" align="center" :key="`report-${i}`" class="mt-3">
                    <v-col class="ellipsis">
                      <a :href="`https://files.nestorr.io/attachments/${attachment.uid}/${attachment.filename}`" target="_blank" class="attachment-link">
                        <font-awesome-icon :icon="[`far`, attachment.$icon]" class="attachment-icon mr-2" />
                        <div class="attachment-filename pl-1">{{ attachment.filename }}</div>
                      </a>
                    </v-col>
                    <v-chip small color="orange" outlined class="ml-2 mr-1" v-if="attachment.type === 1">Devis</v-chip>
                    <v-chip small color="green" outlined class="ml-2 mr-1" v-else-if="attachment.type === 2">Facture</v-chip>
                    <v-icon class="attachment-delete" @click="attachmentToDelete = attachment" v-if="canDeleteAttachment">delete</v-icon>
                  </v-row>
                </template>
              </div>
              <div class="pa-4 font-italic" v-else>
                Aucune pièce jointe
              </div>
            </n-card>
          </div>
        </v-row>
        <v-row no-gutters justify="center" align="center" v-else-if="isInterventionNotFound">
          <div class="text-center" style="margin-top: 20%; width: 250px;">
            <div><v-icon x-large>sentiment_very_dissatisfied</v-icon></div>
            <div class="mt-4 font-weight-bold">Intervention introuvable</div>
          </div>
        </v-row>
        <v-row no-gutters justify="center" align="center" v-else>
          <div style="margin-top: 20%; width: 250px;">
            <v-progress-linear
              color="primary"
              indeterminate
              rounded
              height="6"
            ></v-progress-linear>
          </div>
        </v-row>
      </div>
    </v-row>
    <confirm-dialog v-model="hasEquipmentToUnlink" @yes="unlinkEquipment(equipmentToUnlink.id)"></confirm-dialog>
    <confirm-dialog v-model="hasAttachmentToDelete" @yes="deleteAttachment(attachmentToDelete.id)"></confirm-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import InterventionCard from '@/components/Interventions/Card.vue';
import StepCard from '@/components/Interventions/StepCard.vue';
import NoteCard from '@/components/Interventions/NoteCard.vue';
import InterventionChangeStatus from '@/components/Interventions/ChangeStatus.vue';
import InterventionAddComment from '@/components/Interventions/AddComment.vue';
import CloseIntervention from '@/components/Interventions/CloseIntervention.vue';
import InterventionChangeAssignee from '@/components/Interventions/ChangeAssignee.vue';
import EquipmentModal from '@/components/Equipments/EquipmentModal.vue';
import InterventionAddFile from '@/components/Interventions/AddFile.vue';
import InterventionDateDialog from '@/components/Interventions/InterventionDateDialog.vue';
import ReportAddDialog from '@/components/Interventions/Report/AddDialog.vue';
import ReportDialog from '@/components/Interventions/Report/Dialog.vue';

export default {
  name: `Intervention`,
  props: {
    id: {},
    inDialog: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    // intervention: null,
    steps: [],
    dbFiles: [],
    dbReports: [],
    tab: null,
    equipment: null,
    isInterventionNotFound: false,
    isEquipmentModalOpened: false,
    isFetchingAvailableEquipments: false,
    isFetchingLinkedEquipments: false,
    isFetchingFiles: false,
    isFetchingReports: false,
    isFetchingNotes: false,
    isAddingNote: false,
    isSavingNewNote: false,
    equipmentToUnlink: null,
    attachmentToDelete: null,
    searchAvailableEquipments: ``,
    availableEquipmentsMenu: false,
    isConfirmRemoveInterventionDialogOpened: false,
    isRemovingIntervention: false,
    isUpdatingInterventionDate: false,
    isClosingIntervention: false,
    isInterventionDateDialogOpened: false,
    isReportDialogOpened: false,
    isAddingReportDialogOpened: false,
    interventionDateMenu: false,
    interventionDate: null,
    newNoteComment: ``,
    notes: [],
    openedReportId: null,
    isChangingStatusInDialog: false,
    isAddingCommentInDialog: false,
    isAddingAttachmentInDialog: false,
    isAddingEstimateOrInvoiceInDialog: false,
    isChangingAssigneeInDialog: false,
  }),
  computed: {
    ...mapState(`model/user`, [
      `me`,
      `isAtLeastContributor`,
    ]),
    ...mapState(`model/client`, [
      `company`,
      `rights`,
      `isAggregator`,
      `isSelfMaker`,
    ]),
    ...mapState(`ui/mainLayout`, [`apiBaseUrl`, `routerTitle`]),
    ...mapState(`ui/intervention`, {
      storeIntervention: `intervention`,
    }),
    ...mapGetters(`ui/intervention`, [
      `isRealEstateClient`,
    ]),
    ...mapState(`model/client`, {
      slugClient: `company`,
      isSlugClientMaker: `isMaker`,
    }),
    intervention() {
      if (!this.storeIntervention?.id) {
        return null;
      }

      const { storeIntervention } = this;
      const { site } = storeIntervention;
      return {
        ...storeIntervention,
        $floor: storeIntervention.floor || ``,
        $room: storeIntervention.room || ``,
        $placeDetails: storeIntervention.placeDetails || ``,
        site: {
          ...site,
          $address: _.trim(`${site.address || ``} ${site.postalCode || ``} ${site.city || ``}`).replace(/ +/g, ` `),
          $phone1: _.trim(site.phone1 || ``).replace(/ +/g, ` `),
          $tenant: this.isRealEstateClient ? _.trim(site.tenant) || `` : ``,
          $tenantPhone1: this.isRealEstateClient ? _.trim(site.tenantPhone1) || `` : ``,
          $tenantPhone2: this.isRealEstateClient ? _.trim(site.tenantPhone2) || `` : ``,
          $tenantEmail: this.isRealEstateClient ? _.trim(site.tenantEmail) || `` : ``,
          $owner: this.isRealEstateClient ? _.trim(site.owner) || `` : ``,
        },
      };
    },
    interventionId() {
      return parseInt(this.id, 10);
    },
    hasIntervention() {
      return Boolean(this.intervention?.id);
    },
    hasInterventionDate() {
      return this.hasIntervention && this.intervention.plannedStartAt;
    },
    hasPlaceDetails() {
      return this.hasIntervention
             && (
               this.intervention.$floor.length
               || this.intervention.$room.length
               || this.intervention.$placeDetails.length
             );
    },
    isChallenging() {
      return this.hasIntervention && Boolean(this.intervention.ChallengingIntervention_id);
    },
    ...mapGetters(`model/intervention`, [
      `attachmentIcon`,
    ]),
    ...mapState(`ui/intervention`, {
      linkedEquipments: `linkedEquipments`,
      availableEquipments(state) {
        const linkedEquipmentsIds = _.map(this.linkedEquipments, `id`);
        return _.chain(state.availableEquipments)
          .filter(equipment => !_.includes(linkedEquipmentsIds, equipment.id))
          .map(equipment => ({
            ...equipment,
            searchString: _.toLower(_.deburr(`${equipment.number} - ${equipment.label} - ${equipment.brand} - ${equipment.model} - ${equipment.serialNumber}`)),
          }))
          .filter(equipment => _.includes(equipment.searchString, _.toLower(_.deburr(this.searchAvailableEquipments))))
          .value();
      },
    }),
    flavoredSteps() {
      if (!this.hasIntervention) {
        return [];
      }

      return this.steps.map(step => ({
        ...step,
        intervention: this.intervention,
      }));
    },
    files() {
      return this.dbFiles.map(file => ({
        ...file,
        $isReport: false,
        $icon: this.attachmentIcon(file),
      }));
    },
    reports() {
      return _.chain(this.dbReports)
        .map(report => ({
          ...report,
          $isReport: true,
          $date: moment(report.reportDate).format(`DD/MM/YYYY`),
          $hour: moment(report.reportDate).format(`HH:mm`),
        }))
        .value();
    },
    canRightOnMaker() {
      const makerRights = this.me && this.me.rights ? this.me.rights : [];
      const intervention = this.hasIntervention ? this.intervention : {};
      const makerRight = intervention.Maker_id ? _.find(makerRights, [`maker.id`, intervention.Maker_id]) : {};
      return makerRight && makerRight.right && makerRight.right.id === 3;
    },
    canChangeStatus() {
      return this.hasIntervention && this.me && (this.me.client.isRoot || this.isSelfIntervention) && (this.me.clientAdmin || this.isAtLeastContributor);
    },
    canAddComment() {
      return this.hasIntervention && this.me && (this.isAtLeastContributor || this.canRightOnMaker);
    },
    canAddNote() {
      return this.hasIntervention && this.me && (this.isAtLeastContributor || this.canRightOnMaker) && !this.isSelfIntervention;
    },
    isEquipmentsActivated() {
      return this.rights.equipments === true;
    },
    isSelfIntervention() {
      return this.hasIntervention && this.isSelfMaker && this.intervention.Maker_id === this.company.id;
    },
    canChangeAssignee() {
      return this.hasIntervention && this.me && (this.me.client.isRoot || this.isSelfIntervention) && this.me.clientAdmin;
    },
    canLinkEquipment() {
      return this.isEquipmentsActivated && this.hasIntervention && this.me && (this.isAtLeastContributor || this.canRightOnMaker);
    },
    canUnlinkEquipment() {
      return this.isEquipmentsActivated && this.hasIntervention && this.me && ((this.isSlugClientMaker && this.isAtLeastContributor) || this.me.isIndego);
    },
    canAddAttachment() {
      return this.hasIntervention && this.me && (this.isAtLeastContributor || this.canRightOnMaker);
    },
    canDeleteAttachment() {
      return this.hasIntervention && this.me && (((this.isSlugClientMaker || this.isSelfIntervention) && this.isAtLeastContributor) || this.me.isIndego);
    },
    canAddReport() {
      return this.hasIntervention && this.me && ((this.me.client.isRoot && this.isAtLeastContributor) || this.me.isIndego);
    },
    canAddEstimateOrInvoice() {
      return this.hasIntervention && this.me && ((this.me.client.isRoot && this.isAtLeastContributor) || this.me.isIndego);
    },
    canDoOtherThings() {
      return this.hasIntervention && this.me && ((this.me.client.isRoot && this.isAtLeastContributor) || this.me.isIndego);
    },
    canCloseIntervention() {
      return this.hasIntervention && !this.intervention.status.isFinished && this.me && ((!this.isMakerUser && !this.isSelfIntervention && (this.isAtLeastContributor || this.canRightOnMaker)) || this.me.isIndego); // eslint-disable-line
    },
    canRemoveIntervention() {
      return this.hasIntervention && this.me && (((this.me.client.isRoot || this.isSelfIntervention) && this.isAtLeastContributor) || this.me.isIndego);
    },
    canUpdateInterventionDate() {
      return this.hasIntervention && this.me && (this.me.client.isRoot || this.isSelfIntervention) && this.me.clientAdmin;
    },
    isChangingStatus: {
      get() {
        if (this.inDialog) { return this.isChangingStatusInDialog; }
        return this.$route.name === `InterventionsInterventionChangeStatus`;
      },
      set(isChangingStatus) {
        if (this.inDialog) {
          this.isChangingStatusInDialog = isChangingStatus;
        } else if (isChangingStatus) {
          this.$router.push({ name: `InterventionsInterventionChangeStatus` });
        } else {
          this.$router.push({ name: `InterventionsIntervention` });
        }
      },
    },
    isAddingComment: {
      get() {
        if (this.inDialog) { return this.isAddingCommentInDialog; }
        return this.$route.name === `InterventionsInterventionAddComment`;
      },
      set(isAddingComment) {
        if (this.inDialog) {
          this.isAddingCommentInDialog = isAddingComment;
        } else if (isAddingComment) {
          this.$router.push({ name: `InterventionsInterventionAddComment` });
        } else {
          this.$router.push({ name: `InterventionsIntervention` });
        }
      },
    },
    isAddingAttachment: {
      get() {
        if (this.inDialog) { return this.isAddingAttachmentInDialog; }
        return this.$route.name === `InterventionsInterventionAddAttachment`;
      },
      set(isAddingAttachment) {
        if (this.inDialog) {
          this.isAddingAttachmentInDialog = isAddingAttachment;
        } else if (isAddingAttachment) {
          this.$router.push({ name: `InterventionsInterventionAddAttachment` });
        } else {
          this.$router.push({ name: `InterventionsIntervention` });
        }
      },
    },
    isAddingEstimateOrInvoice: {
      get() {
        if (this.inDialog) { return this.isAddingEstimateOrInvoiceInDialog; }
        return this.$route.name === `InterventionsInterventionAddEstimateOrInvoice`;
      },
      set(isAddingEstimateOrInvoice) {
        if (this.inDialog) {
          this.isAddingEstimateOrInvoiceInDialog = isAddingEstimateOrInvoice;
        } else if (isAddingEstimateOrInvoice) {
          this.$router.push({ name: `InterventionsInterventionAddEstimateOrInvoice` });
        } else {
          this.$router.push({ name: `InterventionsIntervention` });
        }
      },
    },
    isChangingAssignee: {
      get() {
        if (this.inDialog) { return this.isChangingAssigneeInDialog; }
        return this.$route.name === `InterventionsInterventionChangeAssignee`;
      },
      set(isChangingAssignee) {
        if (this.inDialog) {
          this.isChangingAssigneeInDialog = isChangingAssignee;
        } else if (isChangingAssignee) {
          this.$router.push({ name: `InterventionsInterventionChangeAssignee` });
        } else {
          this.$router.push({ name: `InterventionsIntervention` });
        }
      },
    },
    hasEquipmentToUnlink: {
      get() { return this.equipmentToUnlink !== null; },
      set() { this.equipmentToUnlink = null; },
    },
    hasAttachmentToDelete: {
      get() { return this.attachmentToDelete !== null; },
      set() { this.attachmentToDelete = null; },
    },
  },
  methods: {
    goToInterventions() {
      this.$router.push(`/interventions`);
    },
    goToChallengingIntervention() {
      this.$router.push({
        name: `InterventionsChallengingIntervention`,
        params: {
          challengingInterventionId: this.intervention.ChallengingIntervention_id,
        },
      });
    },
    interventionChanged() {
      this.$emit(`change`);
    },
    async unlinkEquipment(equipmentId) {
      this.isFetchingLinkedEquipments = true;
      const equipment = _.find(this.linkedEquipments, { id: equipmentId });
      await this.$store.dispatch(`ui/intervention/unlinkEquipment`, equipment);
      this.interventionChanged();
      this.fetchLinkedEquipments();
      this.fetchSteps();
    },
    async tagAdded() {
      // await this.$store.dispatch(`ui/intervention/addTag`, tag);
      this.interventionChanged();
      this.fetchSteps();
    },
    async tagRemoved() {
      // await this.$store.dispatch(`ui/intervention/removeTag`, tag);
      this.interventionChanged();
      this.fetchSteps();
    },
    async deleteAttachment(fileId) {
      await this.$store.dispatch(`model/file/delete`, fileId);
      this.interventionChanged();
      this.fetchFiles();
    },
    commentAdded(step) {
      this.stepAdded(step);
      this.interventionChanged();
      setTimeout(() => { this.isAddingComment = false; }, 200);
    },
    openReport(report) {
      this.openedReportId = report.id;
      this.isReportDialogOpened = true;
    },
    reportCreated(report) {
      this.closeAddReport();
      this.openReport(report);
      this.fetchIntervention();
      this.fetchReports();
      this.fetchSteps();
    },
    closeAddReport() {
      this.isAddingReportDialogOpened = false;
    },
    closeReport() {
      this.isReportDialogOpened = false;
      setTimeout(() => { this.openedReportId = null; }, 100);
    },
    openNewNote() {
      this.isAddingNote = true;
      setTimeout(() => {
        this.$refs.newNoteTextarea.focus();
      }, 10);
    },
    async addNote() {
      this.isSavingNewNote = true;
      const note = await this.$store.dispatch(`ui/intervention/addNote`, this.newNoteComment);
      if (note) { this.notes.push(note); }
      this.isSavingNewNote = false;
      this.isAddingNote = false;
      this.interventionChanged();
      setTimeout(() => {
        this.newNoteComment = ``;
      }, 100);
    },
    noteUpdated(note) {
      const index = _.findIndex(this.notes, { id: note.id });
      if (index !== -1) {
        Vue.set(this.notes, index, note);
      }
    },
    statusChanged(step) {
      this.stepAdded(step);
      this.interventionChanged();
      setTimeout(() => { this.isChangingStatus = false; }, 200);
    },
    assigneeChanged(intervention) {
      this.intervention.Assignee_id = intervention.Assignee_id;
      this.intervention.assignee = intervention.assignee;
      this.isChangingAssignee = false;
      this.interventionChanged();
    },
    estimateOrInvoiceAdded(file) {
      this.isAddingEstimateOrInvoice = false;
      this.fileAdded(file);
      this.interventionChanged();
      setTimeout(() => {
        this.fetchIntervention();
        this.fetchSteps();
      }, 300);
    },
    attachmentAdded(file) {
      this.isAddingAttachment = false;
      this.fileAdded(file);
      this.interventionChanged();
      setTimeout(() => {
        this.fetchIntervention();
        this.fetchSteps();
      }, 300);
    },
    stepAdded(step) {
      this.intervention.status = step.status;
      this.intervention.Status_id = step.status.id;
      this.fetchIntervention();
      this.interventionChanged();
      this.steps.push(step);
    },
    fileAdded(file) {
      this.dbFiles.push(file);
      this.interventionChanged();
    },
    async fetchIntervention() {
      const intervention = await this.$store.dispatch(`model/intervention/fetchOne`, this.interventionId);

      if (intervention) {
        this.isInterventionNotFound = false;
        this.$store.commit(`ui/intervention/SET_INTERVENTION`, intervention);
        // this.intervention = intervention;

        window.document.title = `${this.routerTitle} - ${intervention.displayNum}`;
      } else {
        this.isInterventionNotFound = true;
      }
    },
    async fetchSteps() {
      const steps = await this.$store.dispatch(`model/intervention/fetchSteps`, this.interventionId);

      this.steps = steps;
    },
    async fetchNotes() {
      this.isFetchingNotes = true;
      this.notes = await this.$store.dispatch(`model/intervention/fetchNotes`, this.interventionId);
      this.isFetchingNotes = false;
    },
    openAvailableEquipmentsMenu() {
      this.searchAvailableEquipments = ``;
      this.availableEquipmentsMenu = true;
    },
    async fetchAvailableEquipments() {
      this.isFetchingAvailableEquipments = true;
      await this.$store.dispatch(`ui/intervention/fetchAvailableEquipments`);
      this.isFetchingAvailableEquipments = false;
    },
    async fetchLinkedEquipments() {
      if (this.isEquipmentsActivated) {
        this.isFetchingLinkedEquipments = true;
        await this.$store.dispatch(`ui/intervention/fetchLinkedEquipments`);
        this.isFetchingLinkedEquipments = false;
      }
    },
    async fetchFiles() {
      this.isFetchingFiles = true;
      this.dbFiles = await this.$store.dispatch(`model/file/fetchForIntervention`, this.interventionId);
      this.isFetchingFiles = false;
    },
    async fetchReports() {
      this.isFetchingReports = true;
      this.dbReports = await this.$store.dispatch(`model/intervention/fetchReports`, this.interventionId);
      this.isFetchingReports = false;
    },
    interventionDateChanged() {
      this.isInterventionDateDialogOpened = false;
      this.fetchIntervention();
      this.fetchSteps();
      this.interventionChanged();
    },
    openEquipment(equipment) {
      // this.equipment = equipment;
      this.$store.commit(`ui/equipmentModal/SET_EQUIPMENT`, equipment);
      this.isEquipmentModalOpened = true;
    },
    async linkEquipment(equipment) {
      this.isFetchingLinkedEquipments = true;
      this.availableEquipmentsMenu = false;
      await this.$store.dispatch(`ui/intervention/linkEquipment`, equipment);
      this.fetchLinkedEquipments();
      this.fetchSteps();
      this.interventionChanged();
    },
    // openCloseInterventionDialog() {
    //   this.isClosingIntervention = false;
    //   this.isConfirmRemoveInterventionDialogOpened = true;
    // },
    interventionClosed() {
      this.isClosingIntervention = false;
      this.fetchIntervention();
      this.fetchSteps();
      this.interventionChanged();
    },
    openRemoveInterventionConfirmDialog() {
      this.isRemovingIntervention = false;
      this.isConfirmRemoveInterventionDialogOpened = true;
    },
    async removeIntervention() {
      this.isRemovingIntervention = true;
      await this.$store.dispatch(`ui/intervention/remove`);
      this.isRemovingIntervention = false;
      this.isConfirmRemoveInterventionDialogOpened = false;
      this.goToInterventions();
      this.interventionChanged();
    },
    // async interventionDateChanged() {
    //   this.interventionDateMenu = false;
    //   this.isUpdatingInterventionDate = true;
    //   const plannedDate = moment(this.interventionDate, `YYYY-MM-DD`).format(`YYYYMMDD`);
    //   await this.$store.dispatch(`ui/intervention/updatePlannedDate`, { plannedDate });
    //   await this.fetchIntervention();
    //   this.isUpdatingInterventionDate = false;
    // },
  },

  async beforeMount() {
    this.$store.commit(`ui/intervention/SET_INTERVENTION_ID`, this.interventionId);
    this.$store.commit(`ui/intervention/SET_INTERVENTION`, {});
    await Promise.all([
      this.fetchIntervention(),
      this.fetchSteps(),
      this.fetchNotes(),
      this.fetchLinkedEquipments(),
      this.fetchFiles(),
      this.fetchReports(),
    ]);
  },

  watch: {
    availableEquipmentsMenu() {
      if (this.availableEquipmentsMenu) {
        this.fetchAvailableEquipments();
      }
    },
  },

  components: {
    InterventionCard,
    StepCard,
    NoteCard,
    InterventionChangeStatus,
    InterventionAddComment,
    CloseIntervention,
    InterventionChangeAssignee,
    EquipmentModal,
    InterventionAddFile,
    ReportAddDialog,
    ReportDialog,
    InterventionDateDialog,
  },
};
</script>

<style lang="scss" scoped>
$attachmentIconColor: #2226ca;
$attachmentDeleteColor: #FF0000;

.attachment-link {
  text-decoration: none;

  .attachment-icon {
    color: $attachmentIconColor;
    font-size: 1.2rem;
  }
  .attachment-filename {
    display: inline;
  }

  &:hover {
    .attachment-icon {
      color: darken($attachmentIconColor, 20);
    }
    .attachment-filename {
      text-decoration: underline;
    }
  }
}

.attachment-delete {
  font-size: 1.3rem;
  margin-left: 4px;
  opacity: .8;
  color: desaturate($attachmentDeleteColor, 95);
  &:hover {
    opacity: 1;
    color: $attachmentDeleteColor;
  }
}
</style>

<style lang="scss">
.right-pane {
  width: 400px;
}
.intervention-details-title {
  font-size: 1.55rem;
  font-weight: 500;
  margin: 40px 0 25px 5px;
}
.intervention-nb-indicator {
  display: inline-block;
  background-color: #5D5D5D;
  color: #FFF;
  margin-left: 6px;
  font-size: 11px;
  text-align: center;
  line-height: 19px;
  width: 19px;
  height: 19px;
  border-radius: 100px;
}
.intervention-tabs {
  border-bottom: 2px solid #AAA;
  .v-tabs-bar {
    margin-bottom: -3px !important;
  }
  .v-tab {
    text-transform: none !important;
    letter-spacing: inherit !important;
    padding: 0 50px !important;
    &:not(.v-tab--active) {
      color: #555 !important;
    }
  }
  .v-tabs-slider-wrapper {
    height: 4px !important;
    .v-tabs-slider {
      background-color: #2226ca;
    }
  }
}
</style>
